import { useMemo } from 'react';
import { Alert, Col, Container, Row } from 'react-bootstrap';
import { useRecommendationLab } from '@/apps/hooks/recommendation-lab/useRecommendationLab';
import { useSkinAnalysisPredictValidation } from '@/apps/hooks/recommendation-lab/useSkinAnalysisPredictValidation';
import { useSkinAnalysisValidationCopyPrompts } from '@/apps/hooks/recommendation-lab/useSkinAnalysisValidationCopyPrompts';
import { SkinAnalysisPredictValidationClient } from '@/infrastructure/SkinAnalysisPredictValidationClient';
import { RecommendationLabInputColumn } from './RecommendationLabInputColumn';
import { PredictValidationResultPanel } from './PredictValidationResultPanel';
import { PredictValidationProviderPanel } from '@/pages/recommendation-lab/PredictValidationProviderPanel';
import { SkinAnalysisReportResetCard } from './SkinAnalysisReportResetCard';
import { ValidationCopyPromptEditor } from './ValidationCopyPromptEditor';
import './recommendation-lab.css';
import './recommendation-lab-mobile.css';

export default function RecommendationLabPage() {
  const { inputOptions, isInputOptionsLoading, inputOptionsError, reloadInputOptions } = useRecommendationLab();
  const validation = useSkinAnalysisPredictValidation();
  const copyPrompts = useSkinAnalysisValidationCopyPrompts();
  const client = useMemo(() => new SkinAnalysisPredictValidationClient(), []);
  return (
    <Container fluid className="py-3 recommendation-lab-page">
      <header className="mb-3">
        <h4 className="mb-0">Recommendation Lab</h4>
        <div className="small text-muted">
          {validation.comparisonEnabled
            ? 'Compare end-to-end customer results from the same captured inputs across Gemini 3.1 Pro and Claude Sonnet 4.6.'
            : 'Preview the customer result from the same captured inputs used by Skin Analysis.'}
        </div>
      </header>
      <SkinAnalysisReportResetCard onReset={(email) => client.resetReportLimit(email)} />
      <ValidationCopyPromptEditor sandbox={copyPrompts} />
      <Row>
        <Col {...(validation.comparisonEnabled ? { xxl: 3 } : { lg: 4 })}>
          <RecommendationLabInputColumn
            inputOptions={inputOptions}
            isInputOptionsLoading={isInputOptionsLoading}
            inputOptionsError={inputOptionsError}
            onReloadInputOptions={reloadInputOptions}
            isLoading={validation.isLoading}
            onRunValidation={validation.predict}
            {...(validation.comparisonEnabled ? { runLabel: 'Compare Gemini and Sonnet' } : {})}
          />
        </Col>
        <Col {...(validation.comparisonEnabled ? { xxl: 9 } : { lg: 8 })}>
          {validation.comparisonEnabled ? (
            <>
              <Alert variant="light" className="validation-comparison-note">
                Both APIs receive the same captured input, but each runs planner and product retrieval independently.
                Compare copy quality only when the selected ingredients and products also match.
              </Alert>
              <div className="validation-provider-grid">
                <PredictValidationProviderPanel
                  title="Gemini 3.1 Pro"
                  provider="Google Gemini"
                  result={validation.gemini.result}
                  isLoading={validation.gemini.isLoading}
                  error={validation.gemini.error}
                  latencyMs={validation.gemini.lastLatencyMs}
                />
                <PredictValidationProviderPanel
                  title="Claude Sonnet 4.6"
                  provider="Amazon Bedrock · Anthropic"
                  result={validation.sonnet.result}
                  isLoading={validation.sonnet.isLoading}
                  error={validation.sonnet.error}
                  latencyMs={validation.sonnet.lastLatencyMs}
                />
              </div>
            </>
          ) : (
            <>
              {validation.gemini.error && <Alert variant="danger">{validation.gemini.error}</Alert>}
              {validation.gemini.isLoading && (
                <Alert variant="secondary" role="status">
                  Generating customer-result preview…
                </Alert>
              )}
              {!validation.gemini.result && !validation.gemini.error && !validation.gemini.isLoading && (
                <Alert variant="secondary">Set the captured inputs, then run a customer-result preview.</Alert>
              )}
              {validation.gemini.result && <PredictValidationResultPanel result={validation.gemini.result} />}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
}
